import type { ComponentType } from "react";
import {
  IconBusiness,
  IconFinance,
  IconGoals,
  IconHealth,
  IconJournal,
  IconNotebooks,
  IconNotes,
  IconToday,
  type IconProps,
} from "./icons";

export interface NavItem {
  to: string;
  label: string;
  Icon: ComponentType<IconProps>;
  /** `end` in the router sense: only "/" needs exact matching. */
  end: boolean;
}

/**
 * The sections, in the order the menu shows them.
 *
 * This lives apart from any one navigation component because three of them read
 * it — the drawer, the phone tab bar, and whatever comes next.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: "/", label: "Сьогодні", Icon: IconToday, end: true },
  { to: "/finance", label: "Фінанси", Icon: IconFinance, end: false },
  { to: "/business", label: "Бізнес", Icon: IconBusiness, end: false },
  { to: "/goals", label: "Цілі", Icon: IconGoals, end: false },
  { to: "/health", label: "Здоровʼя", Icon: IconHealth, end: false },
  // English label by the approved mock (2026-08-11) — the one section that
  // keeps its name untranslated, because "Журналювання" is not what it is called.
  { to: "/journal", label: "Journaling", Icon: IconJournal, end: false },
  // Next to Journaling because both are places he writes, and deliberately
  // apart from it: a journal entry is about a day, a note is about an idea that
  // outlives the day it was had (user decision 2026-08-19).
  { to: "/notes", label: "Нотатки", Icon: IconNotes, end: false },
  // Last of the writing places: notes are captured, notebooks are studied in —
  // pages written and drawn on to remember them (user decision 2026-10-14).
  { to: "/notebooks", label: "Зошити", Icon: IconNotebooks, end: false },
];

/**
 * Which sections keep a permanent slot on the phone tab bar. Eight tabs across
 * 390px is 49px each — too narrow to label and too narrow to hit. Four gives
 * every target ~97px, and the burger at the top holds the complete list.
 *
 * The four are chosen by rhythm, not by importance: these are the screens with
 * something to do on them today. Цілі carries the task panel; Journaling is a
 * daily ritual, so it takes the slot Бізнес held (user decision 2026-08-11) —
 * a screen you check, not a screen you write to. Фінанси took the slot Увага
 * held when the insight feed was removed (user decision 2026-08-16).
 */
const PRIMARY_PATHS: readonly string[] = ["/", "/finance", "/goals", "/journal"];

/**
 * Driven by `PRIMARY_PATHS` order, not by `NAV_ITEMS` order: the tab bar is
 * sequenced by daily rhythm (answer → queue → do → earn) while the drawer stays
 * sequenced by domain. Filtering `NAV_ITEMS` instead would silently re-impose
 * the drawer's order on the bar.
 */
export const PRIMARY_NAV: readonly NavItem[] = PRIMARY_PATHS.flatMap(
  (path) => NAV_ITEMS.find((i) => i.to === path) ?? [],
);

/**
 * The drawer/sidebar list, narrowed to what this account may actually open.
 *
 * Бізнес is Vitalik's own numbers — an invited account with `is_owner: false`
 * gets everything else, but this tab would just be a 404 waiting to happen.
 * `PRIMARY_NAV` needs no such filter: it never carried Бізнес to begin with.
 */
export function visibleNavItems(isOwner: boolean): readonly NavItem[] {
  return isOwner ? NAV_ITEMS : NAV_ITEMS.filter((item) => item.to !== "/business");
}
